import React, { FC, memo, useCallback, useState } from 'react';
import { format } from 'date-fns';
import { useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { Dropdown, Layout, Menu, PageHeader } from 'antd';
import { Content } from 'common/StyledComponents';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Link } from 'react-router-dom';
import { BusinessPartnerRoutePrefix, NewBusinessPartnerRoute } from 'routes';
import { isNil } from 'ramda';
import { atom, useAtom } from 'jotai';
import {
  commonPaginationConfig,
  initialPageParams,
  PageParams,
} from 'models/page';
import { purge } from 'lib/ramda/purge';
import { BusinessPartnerFilters } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersFilers';
import Column from 'antd/lib/table/Column';
import { BusinessPartner } from 'models/businessPartner';
import { DeleteOutlined } from '@ant-design/icons';
import { BusinessPartnerDeleteButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerDeleteButton';
import { FormattedMessage } from 'react-intl';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { BusinessPartnerActivateButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerActivateButton';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { UserStatus } from 'models/userStatus';

const businessPartnersFiltersAtom = atom<PageParams>(initialPageParams);

const BusinessPartnersListPage: FC = memo(() => {
  const queryCache = useQueryCache();
  const [pageMetaData, setPageMetaData] = useAtom(businessPartnersFiltersAtom);
  const [filters, setFilters] = useState({});
  const intlMessage = useFormatMessage();

  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnersList, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetch({
        ...pageMetaData,
        ...filters,
      }),
    {
      keepPreviousData: true,
    }
  );

  const invalidateBusinessPartners = useCallback(
    () => queryCache.invalidateQueries(QueryKey.BusinessPartnersList).then(),
    [queryCache]
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <Layout>
      <Content>
        <PageHeader
          title={intlMessage({ id: 'businessPartners' })}
          extra={[
            <Link
              id="button-add-new-business-partner"
              className="ant-btn ant-btn-primary"
              key={NewBusinessPartnerRoute}
              to={NewBusinessPartnerRoute}
            >
              <FormattedMessage
                id="businessPartners.addNew"
                defaultMessage="Add new business partner"
              />
            </Link>,
          ]}
        />

        <BusinessPartnerFilters
          style={{ marginBottom: '1rem' }}
          onSubmit={(values) => {
            setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
            setFilters(purge(values));
          }}
        />

        <NumberedTable
          rowKey="id"
          loading={isFetching}
          dataSource={data?.content}
          pagination={paginationConfig}
          scroll={{ x: 'auto' }}
        >
          <Column
            title={intlMessage({ id: 'name' })}
            dataIndex="name"
            render={(value, record: BusinessPartner) => (
              <Link
                key={`${BusinessPartnerRoutePrefix}/${record.id}`}
                to={`${BusinessPartnerRoutePrefix}/${record.id}`}
              >
                {value}
              </Link>
            )}
          />
          <Column
            title={intlMessage({ id: 'identifier' })}
            dataIndex="uniqueNumber"
          />
          <Column
            title={intlMessage({ id: 'businessPartners.clientType' })}
            dataIndex="parentPartnerId"
            render={(parentPartnerId, record: BusinessPartner) =>
              !isNil(parentPartnerId)
                ? intlMessage({ id: 'businessPartners.clientSubPartner' })
                : record.subaprtnerIds?.length
                ? intlMessage({ id: 'businessPartners.clientEnterprise' })
                : intlMessage({ id: 'businessPartners.clientStandard' })
            }
          />
          <Column
            title={intlMessage({ id: 'registrationDate' })}
            dataIndex="createDate"
            align="center"
            render={(value) => format(value, 'dd.MM.yyyy')}
          />
          <Column
            title={intlMessage({ id: 'status' })}
            dataIndex="active"
            align="center"
            render={(value) =>
              value === true ? (
                <UserStatusTag status={UserStatus.Active} />
              ) : (
                <UserStatusTag status={UserStatus.Deactivated} />
              )
            }
          />
          <Column
            title={intlMessage({ id: 'actions' })}
            key="id"
            align="right"
            render={(value, record: BusinessPartner) => (
              <Dropdown.Button
                overlay={
                  <Menu>
                    <Menu.Item
                      key="activate"
                      id="menu-item-activate-business-partner"
                    >
                      {record.active && (
                        <BusinessPartnerActivateButton
                          actionType="deactivate"
                          businessPartner={record}
                          type="text"
                          onStatusChange={invalidateBusinessPartners}
                        >
                          <FormattedMessage
                            id="businessPartners.deactivate"
                            tagName="span"
                          />
                        </BusinessPartnerActivateButton>
                      )}

                      {!record.active && (
                        <BusinessPartnerActivateButton
                          actionType="activate"
                          businessPartner={record}
                          type="text"
                          onStatusChange={invalidateBusinessPartners}
                        >
                          <FormattedMessage
                            id="businessPartners.activate"
                            tagName="span"
                          />
                        </BusinessPartnerActivateButton>
                      )}
                    </Menu.Item>

                    <Menu.Item
                      key="remove"
                      id="menu-item-remove-business-partner"
                    >
                      <BusinessPartnerDeleteButton
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        businessPartner={record}
                        onDeleted={invalidateBusinessPartners}
                        tooltip={intlMessage({ id: 'businessPartners.delete' })}
                      >
                        <FormattedMessage id="delete" tagName="span" />
                      </BusinessPartnerDeleteButton>
                    </Menu.Item>
                  </Menu>
                }
              />
            )}
          />
        </NumberedTable>
      </Content>
    </Layout>
  );
});

// eslint-disable-next-line import/no-default-export
export default BusinessPartnersListPage;
